import { FC, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { EmptySearchList } from '../../../components/empty-search-list/index';
import { PaginationController } from '../../../components/pagination-controller/index';
import { Tooltip } from '../../../components/tooltip/index';
import {
  DividendFrequency,
  dividendItemsToFetchMap,
  DividendPayoutStatus,
  DividendPeriodFilter,
  DividendSortOrder,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks, usePagination } from '../../../hooks/index';
import { StringKey } from '../../../lang/index';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';
import { StatusLabel } from './dividend-status-badge';

type SortField = 'approvalDate' | 'payoutDate' | 'stakeholderName';

type Props = {
  companyId: string;
  currencySymbol: string;
  period?: DividendPeriodFilter;
  search: string;
  shareClassId?: string;
  status?: '' | DividendPayoutStatus;
};

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const EMPTY_VALUE = '—';

const cellClass =
  'px-4 py-6 text-sm font-[450] text-gray-700 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]';
const headerClass = 'px-4 text-left text-label-md font-medium text-[#172335]';

const formatPeriod = (
  frequency: DividendFrequency,
  year: number,
  periodIndex: null | number,
): string => {
  if (frequency === DividendFrequency.QUARTERLY) return `Q${periodIndex ?? ''} ${year}`;
  if (frequency === DividendFrequency.SEMI_ANNUALLY) return `H${periodIndex ?? ''} ${year}`;
  if (frequency === DividendFrequency.MONTHLY && periodIndex) {
    return `${MONTH_ABBR[periodIndex - 1]} ${year}`;
  }
  return String(year);
};

const formatDate = (value: null | string): string =>
  value ? new Date(value).toLocaleDateString('en-GB') : EMPTY_VALUE;

const SortableHeader: FC<{
  field: SortField;
  label: StringKey;
  onSort: (field: SortField) => void;
  sortField: null | SortField;
  sortOrder: DividendSortOrder;
}> = ({ field, label, onSort, sortField, sortOrder }) => {
  const isActive = sortField === field;

  return (
    <th className={headerClass}>
      <button
        className="flex cursor-pointer items-center gap-1"
        onClick={() => onSort(field)}
        type="button"
      >
        <span>
          <AppFormattedMessage id={label} />
        </span>
        <span className="relative flex size-4 items-center justify-center">
          {isActive ? (
            <ChevronDownIcon
              className={twMerge('w-2', sortOrder === DividendSortOrder.ASC && 'rotate-180')}
            />
          ) : (
            <>
              <ChevronDownIcon className="absolute -top-[3px] w-2 rotate-180" />
              <ChevronDownIcon className="absolute -bottom-[3px] w-2" />
            </>
          )}
        </span>
      </button>
    </th>
  );
};

export const DividendsByStakeholderTable: FC<Props> = ({
  companyId,
  currencySymbol,
  period,
  search,
  shareClassId,
  status,
}) => {
  const [currentPage, setCurrentPage, resetPage] = usePagination('dividends-by-stakeholder');
  const [itemsToFetch, setItemsToFetch] = useState(dividendItemsToFetchMap[0]);
  const [sortField, setSortField] = useState<null | SortField>(null);
  const [sortOrder, setSortOrder] = useState<DividendSortOrder>(DividendSortOrder.DESC);

  useEffect(() => {
    resetPage();
  }, [period, search, shareClassId, status, sortField, sortOrder, resetPage]);

  const { records, totalPages, isLoading } = DividendHooks.useDividendStakeholderRows({
    companyId,
    period,
    search: search || undefined,
    shareClassId: shareClassId || undefined,
    skip: (currentPage - 1) * itemsToFetch,
    sortBy: sortField ?? undefined,
    sortOrder: sortField ? sortOrder : undefined,
    status: status || undefined,
    take: itemsToFetch,
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) =>
        prev === DividendSortOrder.ASC ? DividendSortOrder.DESC : DividendSortOrder.ASC,
      );
    } else {
      setSortField(field);
      setSortOrder(DividendSortOrder.ASC);
    }
  };

  const formatAmount = (value: number) => `${currencySymbol}${formatDividendAmount(value)}`;

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-400">
        <AppFormattedMessage id="LOADING" />
      </div>
    );
  }

  if (records.length === 0) {
    return <EmptySearchList className="m-auto my-20" />;
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full shrink-0 text-nowrap">
        <thead className="h-11 border-y border-[#F2F2F2] bg-gray-50">
          <tr>
            <SortableHeader
              field="stakeholderName"
              label="STAKEHOLDER"
              onSort={handleSort}
              sortField={sortField}
              sortOrder={sortOrder}
            />
            <th className={headerClass}>
              <AppFormattedMessage id="PAYOUT_NAME" />
            </th>
            <th className={headerClass}>
              <AppFormattedMessage id="SHARE_CLASS" />
            </th>
            <th className={headerClass}>
              <AppFormattedMessage id="PERIOD" />
            </th>
            <th className={headerClass}>
              <AppFormattedMessage id="TOTAL_PAYOUT" />
            </th>
            <SortableHeader
              field="approvalDate"
              label="APPROVAL_DATE"
              onSort={handleSort}
              sortField={sortField}
              sortOrder={sortOrder}
            />
            <SortableHeader
              field="payoutDate"
              label="PAYOUT_DATE"
              onSort={handleSort}
              sortField={sortField}
              sortOrder={sortOrder}
            />
            <th className={headerClass}>
              <AppFormattedMessage id="STATUS" />
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, index) => (
            <tr className="hover:bg-gray-50" key={`${row.stakeholderId}-${row.payoutId}-${index}`}>
              <td className={cellClass}>
                <Tooltip content={row.stakeholderName}>
                  <span className="block max-w-[220px] truncate">{row.stakeholderName}</span>
                </Tooltip>
              </td>
              <td className={cellClass}>
                <Tooltip content={row.payoutName}>
                  <span className="block max-w-[220px] truncate">{row.payoutName}</span>
                </Tooltip>
              </td>
              <td className={cellClass}>
                <Tooltip content={row.shareClassName ?? EMPTY_VALUE}>
                  <span className="block max-w-[200px] truncate">
                    {row.shareClassName ?? EMPTY_VALUE}
                  </span>
                </Tooltip>
              </td>
              <td className={cellClass}>
                {formatPeriod(row.frequency, row.year, row.periodIndex)}
              </td>
              <td className={cellClass}>{formatAmount(row.totalAmount)}</td>
              <td className={cellClass}>{formatDate(row.approvalDate)}</td>
              <td className={cellClass}>{formatDate(row.payoutDate)}</td>
              <td className={cellClass}>
                <StatusLabel status={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="px-4 pt-4">
        <PaginationController
          currentPage={currentPage}
          itemsCount={records.length}
          itemsToFetch={itemsToFetch}
          itemsToFetchMap={dividendItemsToFetchMap}
          onClick={setCurrentPage}
          onFetchAmountChange={setItemsToFetch}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};
